import React from 'react';
import { useSelector } from 'react-redux';
import Title from '../../components/Title';
import { useSupportTicket } from '../../hooks/useSupportTicket';
import TicketList from './TicketList';
import ChatHeader from './ChatHeader';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import UserProfile from './UserProfileComponent';

const SupportTicket = () => {
  const { userData } = useSelector(state => state.auth);
  const {
    message,
    selectedTicket,
    filteredTickets,
    activeFilter,
    file,
    uploading,
    showMoreMenu,
    loading,
    error,
    fileInputRef,
    moreMenuRef,
    setMessage,
    handleSelectTicket,
    handleFileChange,
    handleSendReply,
    handleFilterChange,
    handleStatusChange,
    handleDeleteTicket,
    toggleMoreMenu,
    setFile,
    setShowMoreMenu,
  } = useSupportTicket(userData);


  const sortedTickets = React.useMemo(() => {
    return [...filteredTickets].sort((a, b) => {
      if (a.isRead !== b.isRead) {
        return a.isRead ? 1 : -1;
      }
      const aLatestTime = new Date(a.updatedAt).getTime();
      const bLatestTime = new Date(b.updatedAt).getTime();
      return bLatestTime - aLatestTime;
    });
  }, [filteredTickets]);

  return (
    <div className="p-4 sm:ml-64 w-[80%] bg-[#F4F5F6] font-nohemi">
      <div className="">
        <Title title="Support" />
      </div>
      <div className="p-2 bg-white">
        <div className="flex w-full bg-[#F4F5F6]" style={{ height: 'calc(100vh - 120px)' }}>
          {/* Left Panel - Ticket List */}
          <TicketList
            sortedTickets={sortedTickets}
            selectedTicket={selectedTicket}
            activeFilter={activeFilter}
            handleSelectTicket={handleSelectTicket}
            handleFilterChange={handleFilterChange}
          />

          {/* Chat Area */}
          <div className="flex flex-col flex-1">
            {selectedTicket ? (
              <>
                {/* Chat Header */}
                <ChatHeader
                  selectedTicket={selectedTicket}
                  showMoreMenu={showMoreMenu}
                  toggleMoreMenu={toggleMoreMenu}
                  moreMenuRef={moreMenuRef}
                  handleStatusChange={handleStatusChange}
                  handleDeleteTicket={handleDeleteTicket}
                  setShowMoreMenu={setShowMoreMenu}
                />

                {/* Messages */}
                <div className="flex-1 overflow-y-auto">
                  <MessageList
                    selectedTicket={selectedTicket}
                    userData={userData}
                    loading={loading}
                  />
                </div>

                {/* Message Input */}
                <MessageInput
                  message={message}
                  setMessage={setMessage}
                  file={file}
                  setFile={setFile}
                  uploading={uploading}
                  fileInputRef={fileInputRef}
                  handleFileChange={handleFileChange}
                  handleSendReply={handleSendReply}
                  selectedTicket={selectedTicket}
                />
              </>
            ) : (
              <div className="flex items-center justify-center flex-1 bg-white">
                <div className="text-center text-gray-500">
                  <div className="mb-4">
                    <svg
                      className="w-16 h-16 mx-auto text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-lg font-medium text-gray-900">
                    Select a ticket to start chatting
                  </h3>
                  <p className="text-sm text-gray-500">
                    Choose a support ticket from the list to view and respond to messages.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - User Profile */}
          {selectedTicket && (
            <div className="border-l border-gray-200">
              <UserProfile selectedTicket={selectedTicket} userData={userData} />
            </div>
          )}
        </div>

        {error && (
          <div className="fixed px-4 py-3 text-red-700 bg-red-100 border border-red-400 rounded shadow-lg bottom-4 right-4">
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium">Error: </span>
              <span>{error}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTicket;
